import React from 'react';
import { Grid, Typography } from '@material-ui/core';
import { SectionCard, SectionLabel, KpiRow, TEXT, SPACE, SURFACE } from '../../shared_components';
import '../../shared_components/assets/styles/plannerTracker.scss';

/*
 * Color roles for this dashboard (see the data-viz color formula: categorical =
 * identity, status = state). Kept local to the dashboard rather than folded into
 * the app-wide PLANNER_COLORS, since those mix identity and status meanings —
 * this page needs them kept apart so a bar's color never doubles as a verdict.
 */
const CATEGORICAL = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#4a3aa7'];
const MUTED = '#898781';
const STATUS = { good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b' };

const withColors = (rows) => rows.map((row, index) => ({ ...row, color: row.color || CATEGORICAL[index % CATEGORICAL.length] }));

/* WCAG relative luminance — picks black/white bar-fill text by actual contrast rather than assuming white always reads. */
const relLuminance = (hex) => {
  const [r, g, b] = hex.slice(1).match(/.{2}/g).map((h) => {
    const v = parseInt(h, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrastRatio = (l1, l2) => (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
const fillTextColor = (hex) => {
  const l = relLuminance(hex);
  return contrastRatio(l, 1) >= contrastRatio(l, 0) ? '#ffffff' : '#1a1d29';
};

const KPI_CARDS = [
  { key: 'plans_created', label: 'Plans Created (12mo)', value: 47, sub: '↑ 18% vs prior year', color: CATEGORICAL[0] },
  { key: 'projects_closed', label: 'Projects Closed', value: 31, sub: '↑ 12% vs prior year', color: STATUS.good },
  { key: 'avg_duration', label: 'Avg. Project Duration', value: '52d', sub: 'From launch to closure', color: CATEGORICAL[1] },
  { key: 'on_time_rate', label: 'On-Time Launch Rate', value: '78%', sub: 'Within planned start date', color: CATEGORICAL[5] },
];

const PROJECTS_BY_LESSOR = withColors([
  { label: 'Avolon', count: 14 },
  { label: 'BBAM', count: 9 },
  { label: 'AVIC', count: 7 },
  { label: 'Castlelake', count: 5 },
  { label: 'ALC', count: 3 },
]);

const PROJECTS_BY_INSPECTION_TYPE = withColors([
  { label: 'Mid-Term Inspection', count: 18 },
  { label: 'Pre-Purchase', count: 12 },
  { label: 'Borescope', count: 8 },
  { label: 'Records Review', count: 6 },
  { label: 'Physical Only', count: 3 },
]);

const ASSETS_BY_LOCATION = withColors([
  { label: 'Singapore (Changi)', count: 22 },
  { label: 'Dublin', count: 18 },
  { label: 'Frankfurt (MRO)', count: 15 },
  { label: 'Dubai', count: 12 },
  { label: 'Doha', count: 11 },
  { label: 'Other', count: 9, color: MUTED },
]);

const ASSETS_BY_STATUS = [
  { label: 'In Service', count: 45, color: STATUS.good },
  { label: 'Under Inspection', count: 18, color: STATUS.warning },
  { label: 'Available', count: 16, color: CATEGORICAL[0] },
  { label: 'Off-lease / Sold', count: 8, color: STATUS.critical },
];

const PROJECTS_BY_AC_TYPE = withColors([
  { label: 'A320 Family', count: 17 },
  { label: 'A350', count: 11 },
  { label: 'B777', count: 9 },
  { label: 'B737 MAX', count: 7 },
  { label: 'B787', count: 3 },
]);

const PROJECTS_BY_ENGINE_TYPE = withColors([
  { label: 'CFM56', count: 15 },
  { label: 'LEAP-1A', count: 9 },
  { label: 'Trent XWB', count: 8 },
  { label: 'GE90', count: 6 },
  { label: 'GEnx', count: 4 },
]);

const DURATION_METRICS = [
  { label: 'Avg. Planning Duration', value: '8.4 days' },
  { label: 'Avg. Inspection Duration', value: '22.1 days' },
  { label: 'Avg. Report Formation', value: '11.3 days' },
  { label: 'Avg. Client Acceptance', value: '9.7 days' },
  { label: 'Avg. Total End-to-End', value: '52.0 days', color: CATEGORICAL[0] },
];

const REVENUE_CYCLE_METRICS = [
  { label: 'Closed → Invoice Raised', value: '4.2 days', color: STATUS.good },
  { label: 'Invoice Raised → Paid', value: '18.5 days' },
  { label: 'Projects Invoice Pending', value: 3, color: STATUS.critical },
  { label: 'Projects Invoice Raised', value: 2, color: STATUS.warning },
  { label: 'Financially Closed', value: 18, color: STATUS.good },
];

const INACTIVITY_METRICS = [
  { label: '72h Alerts Sent (12mo)', value: 14 },
  { label: '120h Escalations', value: 5, color: STATUS.critical },
  { label: 'Avg. Response Time', value: '6.2 hours', color: STATUS.good },
  { label: 'Projects Resolved <24h', value: 11, color: STATUS.good },
  { label: 'Currently Active Alerts', value: 2, color: STATUS.critical },
];

const ESCALATION_METRICS = [
  { label: 'Total Raised (12mo)', value: 6, color: STATUS.critical },
  { label: 'Castlelake', value: 3 },
  { label: 'Avolon', value: 2 },
  { label: 'BBAM', value: 1 },
  { label: 'Avg. Resolution Time', value: '3.1 days', color: STATUS.good },
];

const Panel = ({ title, children }) => (
  <SectionCard noPadding style={{ height: '100%' }}>
    <div style={{ padding: '14px 18px', borderBottom: `1px solid ${SURFACE.divider}` }}>
      <Typography variant="subtitle2" style={{ fontWeight: 700, color: TEXT.primary }}>{title}</Typography>
    </div>
    <div style={{ padding: 18 }}>{children}</div>
  </SectionCard>
);

const BarBreakdown = ({ rows }) => {
  const max = Math.max(...rows.map((row) => row.count), 1);
  return rows.map((row, index) => (
    <div
      key={row.label}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
        borderBottom: index === rows.length - 1 ? 'none' : `1px solid ${SURFACE.divider}`,
      }}
    >
      <Typography variant="body2" style={{ width: 160, flexShrink: 0, color: TEXT.secondary }}>{row.label}</Typography>
      <div style={{ flex: 1, height: 20, backgroundColor: '#eef0f4', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${(row.count / max) * 100}%`, backgroundColor: row.color, borderRadius: 4,
          display: 'flex', alignItems: 'center', paddingLeft: 8, boxSizing: 'border-box',
        }}
        >
          <Typography variant="caption" style={{ color: fillTextColor(row.color), fontWeight: 700 }}>{row.count}</Typography>
        </div>
      </div>
      <Typography variant="body2" style={{ fontWeight: 700, color: TEXT.primary, minWidth: 24, textAlign: 'right' }}>{row.count}</Typography>
    </div>
  ));
};

const MetricList = ({ metrics }) => metrics.map((metric, index) => (
  <div
    key={metric.label}
    style={{
      display: 'flex', justifyContent: 'space-between', padding: '10px 0',
      borderBottom: index === metrics.length - 1 ? 'none' : `1px solid ${SURFACE.divider}`,
    }}
  >
    <Typography variant="body2" style={{ color: TEXT.secondary }}>{metric.label}</Typography>
    <Typography variant="body2" style={{ fontWeight: 700, color: metric.color || TEXT.primary }}>{metric.value}</Typography>
  </div>
));

const DashboardAnalytics = () => (
  <div className="pt-scroll-panel pt-scroll-panel--dashboard">
    <KpiRow cards={KPI_CARDS} />

    <Grid container spacing={2} style={{ marginBottom: SPACE.md }}>
      <Grid item xs={12} md={6}>
        <Panel title="📊 Projects by Lessor"><BarBreakdown rows={PROJECTS_BY_LESSOR} /></Panel>
      </Grid>
      <Grid item xs={12} md={6}>
        <Panel title="📊 Projects by Inspection Type"><BarBreakdown rows={PROJECTS_BY_INSPECTION_TYPE} /></Panel>
      </Grid>
    </Grid>

    <SectionLabel action={<Typography variant="caption" style={{ color: TEXT.tertiary }}>87 assets across registry</Typography>}>
      ✈️ Asset Portfolio
    </SectionLabel>
    <Grid container spacing={2} style={{ marginBottom: SPACE.md }}>
      <Grid item xs={12} md={6}>
        <Panel title="📍 Assets by Location"><BarBreakdown rows={ASSETS_BY_LOCATION} /></Panel>
      </Grid>
      <Grid item xs={12} md={6}>
        <Panel title="📊 Assets by Status">
          <BarBreakdown rows={ASSETS_BY_STATUS} />
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${SURFACE.divider}`, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" style={{ color: TEXT.secondary }}>Linked to active projects</Typography>
            <Typography variant="body2" style={{ fontWeight: 700, color: TEXT.primary }}>34 of 87</Typography>
          </div>
        </Panel>
      </Grid>
    </Grid>

    <Grid container spacing={2} style={{ marginBottom: SPACE.md }}>
      <Grid item xs={12} md={6}>
        <Panel title="✈️ Projects by A/C Type"><BarBreakdown rows={PROJECTS_BY_AC_TYPE} /></Panel>
      </Grid>
      <Grid item xs={12} md={6}>
        <Panel title="⚙️ Projects by Engine Type"><BarBreakdown rows={PROJECTS_BY_ENGINE_TYPE} /></Panel>
      </Grid>
    </Grid>

    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Panel title="⏱ Duration Metrics"><MetricList metrics={DURATION_METRICS} /></Panel>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Panel title="💰 Revenue Cycle"><MetricList metrics={REVENUE_CYCLE_METRICS} /></Panel>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Panel title="🔔 Inactivity Stats"><MetricList metrics={INACTIVITY_METRICS} /></Panel>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Panel title="📢 Client Escalations"><MetricList metrics={ESCALATION_METRICS} /></Panel>
      </Grid>
    </Grid>
  </div>
);

export default DashboardAnalytics;
